import React, {useState, useEffect} from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Card,
  CardContent,
  Chip,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
  Backdrop,
  FormHelperText
} from '@mui/material';
import {
  People,
  Add,
  Close,
  Edit,
  Delete
} from '@mui/icons-material';

const AgeRulesTab = (props) => {
  // Modal state
  const [openAgeRuleModal, setOpenAgeRuleModal] = useState(false);
  const [selectedAgeRuleForEdit, setSelectedAgeRuleForEdit] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [ageRuleToDelete, setAgeRuleToDelete] = useState(null);

  // State for notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Handle success states
  useEffect(() => {
    if (props.isSuccess) {
      setSnackbarMessage('Age rule operation completed successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Close modal and reset form data on success
      setOpenAgeRuleModal(false);
      setSelectedAgeRuleForEdit(null);
      setAgeRuleData({
        max_age: "",
        relationship: [],
        at_age_action: "Convert_To_Adult",
        when: "OnAnniversary",
        notify: true,
        settlement: "None",
      });
      // Reset validation state
      setErrors({});
      setTouched({});
      
      // Reset success state after showing notification
      setTimeout(() => {
        props.resetAgeRuleSuccess && props.resetAgeRuleSuccess();
      }, 100);
    }
  }, [props.isSuccess]);

  // Handle error states
  useEffect(() => {
    if (props.isError) {
      setSnackbarMessage(`Error: ${props.isError.message || 'Something went wrong'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      
      // Reset error state after showing notification
      setTimeout(() => {
        props.resetAgeRuleError && props.resetAgeRuleError();
      }, 100);
    }
  }, [props.isError]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Age Rules state
  const [ageRuleData, setAgeRuleData] = useState({
    max_age: "",
    relationship: [],
    at_age_action: "Convert_To_Adult",
    when: "OnAnniversary",
    notify: true,
    settlement: "None",
  });

  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Use age rules data from props if available
  const ageRules = props.ageRules || [];

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    // Max age validation
    if (!ageRuleData.max_age || ageRuleData.max_age === "") {
      newErrors.max_age = "Maximum age is required";
    } else if (parseInt(ageRuleData.max_age) <= 0) {
      newErrors.max_age = "Maximum age must be greater than 0";
    } else if (parseInt(ageRuleData.max_age) > 150) {
      newErrors.max_age = "Maximum age cannot exceed 150";
    }

    // Relationship validation
    if (!ageRuleData.relationship || ageRuleData.relationship.length === 0) {
      newErrors.relationship = "At least one relationship must be selected";
    }

    // Action validation
    if (!ageRuleData.at_age_action) {
      newErrors.at_age_action = "Action is required";
    }

    // Timing validation
    if (!ageRuleData.when) {
      newErrors.when = "Timing is required";
    }

    // Settlement validation
    if (!ageRuleData.settlement) {
      newErrors.settlement = "Settlement option is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to get field error
  const getFieldError = (fieldName) => {
    return touched[fieldName] && errors[fieldName] ? errors[fieldName] : "";
  };

  // Helper function to check if field has error
  const hasFieldError = (fieldName) => {
    return touched[fieldName] && Boolean(errors[fieldName]);
  };


    // Age Rules handlers
  const handleAgeRuleInputChange = (field) => (event) => {
    // Mark field as touched
    setTouched(prev => ({ ...prev, [field]: true }));

    let value = event.target.value;
    
    // For number fields, keep as string if empty, convert to number if filled
    if (event.target.type === 'number') {
      if (value === "") {
        value = "";
      } else {
        const numValue = parseFloat(value);
        value = isNaN(numValue) ? "" : numValue;
      }
    }

    setAgeRuleData({
      ...ageRuleData,
      [field]: value,
    });

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleAgeRuleRelationshipChange = (relationship) => (event) => {
    // Mark relationship field as touched
    setTouched(prev => ({ ...prev, relationship: true }));

    const isChecked = event.target.checked;
    let updatedRelationships = [...ageRuleData.relationship];
    
    if (isChecked) {
      if (!updatedRelationships.includes(relationship)) {
        updatedRelationships.push(relationship);
      }
    } else {
      updatedRelationships = updatedRelationships.filter(rel => rel !== relationship);
    }
    
    setAgeRuleData({
      ...ageRuleData,
      relationship: updatedRelationships,
    });

    // Clear error for relationship field when user makes selection
    if (errors.relationship && updatedRelationships.length > 0) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.relationship;
        return newErrors;
      });
    }
  };

  const handleAgeRuleNotificationChange = (event) => {
    setAgeRuleData({
      ...ageRuleData,
      notify: event.target.checked,
    });
  };

  const handleAddAgeRule = () => {
    // Mark all fields as touched to show errors
    console.log("Submitting age rule with data:", ageRuleData, "selected for edit:", selectedAgeRuleForEdit);
    const allFields = ['max_age', 'relationship', 'at_age_action', 'when', 'settlement'];
    const touchedState = {};
    allFields.forEach(field => {
      touchedState[field] = true;
    });
    setTouched(touchedState);

    // Validate form
    if (!validateForm()) {
      // Form has errors, don't submit
      return;
    }

    const ageRulePayload = {
      max_age: parseInt(ageRuleData.max_age),
      relationship: ageRuleData.relationship,
      at_age_action: ageRuleData.at_age_action,
      when: ageRuleData.when,
      notify: ageRuleData.notify,
      settlement: ageRuleData.settlement,
    };

    if (selectedAgeRuleForEdit) {
      // Edit existing rule
      if (props.handleEditAgeRules) {
        console.log("Editing Age Rule:", ageRulePayload);
        props.handleEditAgeRules({ ...ageRulePayload, rule_guid: selectedAgeRuleForEdit.guid });
      }
    } else {
      // Add new rule
      if (props.handleAddAgeRules) {
        console.log("Creating Age Rule:", ageRulePayload);
        props.handleAddAgeRules(ageRulePayload);
      }
    }
    
    // Don't close the modal here - let the success/error handlers manage the modal state
  };

  const handleDeleteAgeRule = (age_rule_guid) => {
    if (props.handleDeleteAgeRules) {
      console.log("Deleting Age Rule:", age_rule_guid);
      props.handleDeleteAgeRules(age_rule_guid);
    }
  };

  const handleOpenAgeRuleModal = (ageRule = null) => {
    if (ageRule) {
      setSelectedAgeRuleForEdit(ageRule);
      setAgeRuleData({
        max_age: ageRule.max_age || "",
        relationship: ageRule.relationship || [],
        at_age_action: ageRule.at_age_action || "Convert_To_Adult",
        when: ageRule.when || "OnAnniversary",
        notify: ageRule.notify !== undefined ? ageRule.notify : true,
        settlement: ageRule.settlement || "None",
      });
    } else {
      setAgeRuleData({
        max_age: "",
        relationship: [],
        at_age_action: "Convert_To_Adult",
        when: "On_Anniversary",
        notify: true,
        settlement: "None",
      });
      setSelectedAgeRuleForEdit(null);
    }
    setOpenAgeRuleModal(true);
  };

  const handleCloseAgeRuleModal = () => {
    setOpenAgeRuleModal(false);
    setSelectedAgeRuleForEdit(null);
    setAgeRuleData({
      max_age: "",
      relationship: [],
      at_age_action: "Convert_To_Adult",
      when: "OnAnniversary",
      notify: true,
      settlement: "None",
    });
    // Reset validation state
    setErrors({});
    setTouched({});
  };

  const handleDeleteClick = (ageRule) => {
    setAgeRuleToDelete(ageRule);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (ageRuleToDelete) {
      handleDeleteAgeRule(ageRuleToDelete.guid);
      setDeleteConfirmOpen(false);
      setAgeRuleToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setAgeRuleToDelete(null);
  };

  const hasAgeRules = ageRules && ageRules.length > 0;

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 4 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Age Validation Rules Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Define automated actions based on member age thresholds
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenAgeRuleModal()}
           sx={{
            textTransform: "none",
            backgroundColor: "#fba92c",
            "&:hover": {
              backgroundColor: "#fba82c",
            },
            px: 3,
            py: 1.5,
            borderRadius: 2,
          }}
          >
            Add Age Rule
          </Button>
        </Stack>
      </Box>

      {hasAgeRules ? (
        <Box>
          <Stack spacing={3}>
            {ageRules.map((rule, index) => (
              <Card 
                key={rule.guid || index} 
                sx={{ 
                  border: "1px solid #e5e7eb", 
                  borderRadius: 2,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    borderColor: "#C9C9C9",
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: "#F8F9FA",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}>
                        <People sx={{ fontSize: 20, color: "#173E4B" }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Max Age {rule.max_age} Rule
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: rule.notify ? "#dcfce7" : "#fee2e2",
                            color: rule.notify ? "#166534" : "#991b1b",
                            px: 2,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            display: "inline-block",
                          }}
                        >
                          {rule.notify ? "Notify" : "Silent"}
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton 
                        size="small" 
                        sx={{ color: "#6b7280" }}
                        onClick={() => handleOpenAgeRuleModal(rule)}
                        title="Edit age rule"
                      >
                        <Edit sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        sx={{ color: "#ef4444" }}
                        onClick={() => handleDeleteClick(rule)}
                        title="Delete age rule"
                      >
                        <Delete sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Age Rule Details */}
                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2, mb: 3 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Action
                        </Typography>
                        <Typography variant="body2">
                          {rule.at_age_action}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Timing
                        </Typography>
                        <Typography variant="body2">
                          {rule.when}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Settlement
                        </Typography>
                        <Typography variant="body2">
                          {rule.settlement}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Affected Relationships */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 1, display: "block" }}>
                        Affected Relationships
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {rule.relationship && rule.relationship.length > 0 ? 
                          rule.relationship.map((relationship, relIndex) => (
                            <Chip 
                              key={relIndex} 
                              label={relationship} 
                              size="small" 
                              variant="outlined" 
                              sx={{ fontSize: "0.75rem" }}
                            />
                          )) : 
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                            No relationships defined
                          </Typography>
                        }
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>
      ) : (
        <Box
          sx={{
            border: "2px dashed #d1d5db",
            borderRadius: 2,
            p: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 300,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              backgroundColor: "#f3f4f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <People sx={{ fontSize: 24, color: "#6b7280" }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            No age rules yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: "center" }}>
            Get started by creating your first age validation rule.<br />
            Age rules automate actions when members reach certain age thresholds.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => handleOpenAgeRuleModal()}
            sx={{
              textTransform: "none",
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
              px: 3,
              py: 1,
            }}
          >
            Create Age Rule
          </Button>
        </Box>
      )}

      {/* Age Rule Creation/Edit Modal */}
      <Dialog
        open={openAgeRuleModal && !props.isLoading }
        onClose={handleCloseAgeRuleModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {selectedAgeRuleForEdit ? "Edit Age Rule" : "Create Age Rule"}
            </Typography>
            <IconButton onClick={handleCloseAgeRuleModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 3 }}>
          {/* Age Rule Form */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
              * Required fields.
            </Typography>
            
            {/* Age Limit Row */}
            <Box sx={{ mb: 3 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                  Maximum Age Limit *
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  placeholder="21"
                  value={ageRuleData.max_age}
                  onChange={handleAgeRuleInputChange("max_age")}
                  variant="outlined"
                  error={hasFieldError("max_age")}
                  helperText={getFieldError("max_age")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fafafa",
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Affected Relationships */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
                Affected Relationships *
              </Typography>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ageRuleData.relationship.includes("Principal")}
                      onChange={handleAgeRuleRelationshipChange("Principal")}
                      sx={{ color: "#173E4B", "&.Mui-checked": { color: "#173E4B" } }}
                    />
                  }
                  label="Principal"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ageRuleData.relationship.includes("Spouse")}
                      onChange={handleAgeRuleRelationshipChange("Spouse")}
                      sx={{ color: "#173E4B", "&.Mui-checked": { color: "#173E4B" } }}
                    />
                  }
                  label="Spouse"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ageRuleData.relationship.includes("Child")}
                      onChange={handleAgeRuleRelationshipChange("Child")}
                      sx={{ color: "#173E4B", "&.Mui-checked": { color: "#173E4B" } }}
                    />
                  }
                  label="Child"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ageRuleData.relationship.includes("Parent")}
                      onChange={handleAgeRuleRelationshipChange("Parent")}
                      sx={{ color: "#173E4B", "&.Mui-checked": { color: "#173E4B" } }}
                    />
                  }
                  label="Parent"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ageRuleData.relationship.includes("Extended")}
                      onChange={handleAgeRuleRelationshipChange("Extended")}
                      sx={{ color: "#173E4B", "&.Mui-checked": { color: "#173E4B" } }}
                    />
                  }
                  label="Extended"
                />
              </FormGroup>
              {getFieldError("relationship") && (
                <FormHelperText error sx={{ mt: 1 }}>
                  {getFieldError("relationship")}
                </FormHelperText>
              )}
            </Box>

            {/* Action and Timing Row */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mb: 3 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                  Action *
                </Typography>
                <FormControl fullWidth error={hasFieldError("at_age_action")}>
                  <Select
                    value={ageRuleData.at_age_action}
                    onChange={handleAgeRuleInputChange("at_age_action")}
                    sx={{
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <MenuItem value="Convert_To_Adult">Convert To Adult</MenuItem>
                    <MenuItem value="Remove">Remove From Policy</MenuItem>
                  
                  </Select>
                  {getFieldError("at_age_action") && (
                    <FormHelperText>{getFieldError("at_age_action")}</FormHelperText>
                  )}
                </FormControl>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                  Timing *
                </Typography>
                <FormControl fullWidth error={hasFieldError("when")}>
                  <Select
                    value={ageRuleData.when}
                    onChange={handleAgeRuleInputChange("when")}
                    sx={{
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <MenuItem value="OnAnniversary">On Anniversary</MenuItem>
                    <MenuItem value="Immediate">Immediate</MenuItem>
                    <MenuItem value="Next_Billing_Cycle">Next Billing Cycle</MenuItem>
                    <MenuItem value="End_Of_Month">End of Month</MenuItem>
                  </Select>
                  {getFieldError("when") && (
                    <FormHelperText>{getFieldError("when")}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Box>

            {/* Settlement */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Settlement *
              </Typography>
              <FormControl fullWidth error={hasFieldError("settlement")}>
                <Select
                  value={ageRuleData.settlement}
                  onChange={handleAgeRuleInputChange("settlement")}
                  sx={{
                    backgroundColor: "#fafafa",
                  }}
                >
                  <MenuItem value="None">None</MenuItem>
                  <MenuItem value="ProRata">Pro Rata</MenuItem>
                  <MenuItem value="Full_Refund">Full Refund</MenuItem>
                </Select>
                {getFieldError("settlement") && (
                  <FormHelperText>{getFieldError("settlement")}</FormHelperText>
                )}
              </FormControl>
            </Box>

            {/* Notification Toggle */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
                p: 2,
                backgroundColor: "#f9fafb",
                borderRadius: 1,
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                  Send Notification
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {ageRuleData.notify ? "Member will be notified" : "No notification"}
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={ageRuleData.notify}
                    onChange={handleAgeRuleNotificationChange}
                    sx={{
                      color: "#173E4B",
                      "&.Mui-checked": {
                        color: "#173E4B",
                      },
                    }}
                  />
                }
                label=""
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCloseAgeRuleModal}
            disabled={props.isLoading}
            sx={{ 
              textTransform: "none",
              color: "#6b7280",
              "&:disabled": {
                color: "#9ca3af"
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddAgeRule}
            variant="contained"
            disabled={props.isLoading}
            startIcon={props.isLoading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ 
              textTransform: "none",
              backgroundColor: "#fba92c",
              "&:hover": {
                backgroundColor: "#f59e0b",
              },
              "&:disabled": {
                backgroundColor: "#d1d5db",
                color: "#6b7280"
              }
            }}
          >
            {props.isLoading 
              ? "Processing..." 
              : (selectedAgeRuleForEdit ? "Update Rule" : "Create Rule")
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Delete Age Rule
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the age rule with max age limit {ageRuleToDelete && ageRuleToDelete.max_age}? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCancelDelete}
            sx={{ 
              textTransform: "none",
              color: "#6b7280"
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{ 
              textTransform: "none",
              backgroundColor: "#ef4444",
              "&:hover": {
                backgroundColor: "#dc2626",
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={props.isLoading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress color="inherit" />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Processing age rule operation...
          </Typography>
        </Box>
      </Backdrop>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AgeRulesTab;